import { createFileRoute } from "@tanstack/react-router";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState, useRef } from "react";
import { toast } from "sonner";
import { updateFeedback, addFeedback } from "@/store/actions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { selectJobOverviewData, selectTasksByJobId, selectCheckinByBooking } from "@/store/selectors";
import { RootState } from "@/store/store";
import JobTaskListView from "@/components/tasks/list";
import SignaturePad, { SignaturePadRef } from "@/components/ui/signature-pad";

export const Route = createFileRoute("/job/$jobId/$date/signoff/signature")({
  component: JobSignOffSignaturePage,
});

function JobSignOffSignaturePage() {
  const { jobId, date } = Route.useParams();
  const navigate = Route.useNavigate();
  const dispatch = useDispatch();
  const signaturePadRef = useRef<SignaturePadRef>(null);

  const [signedBy, setSignedBy] = useState("");
  const [message, setMessage] = useState("");
  const [hasSignature, setHasSignature] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);

  const { job, booking } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  );

  const checkin = useSelector((state: RootState) =>
    booking ? selectCheckinByBooking(state, booking.id, booking.date) : null
  );

  const tasks = useSelector((state: RootState) =>
    selectTasksByJobId(state, jobId)
  );

  const feedbackToday = useSelector((state: RootState) =>
    state.feedback.items.find(
      (feedback) =>
        booking &&
        feedback.booking_id === booking.id &&
        feedback.booking_date === booking.date
    )
  );

  const handleSignatureChange = (hasSignature: boolean) => {
    setHasSignature(hasSignature);
  };

  const handleComplete = async () => {
    if (!hasSignature || !signedBy.trim()) {
      toast.error("Please provide a signature and name");
      return;
    }

    const signaturePad = signaturePadRef.current;
    if (!signaturePad) return;

    try {
      // Get signature as data URL
      const dataUrl = signaturePad.toDataURL("image/png");

      // Update feedback with signature data
      if (feedbackToday) {
        dispatch(
          updateFeedback({
            ...feedbackToday,
            feedback: feedbackToday.feedback || 'signature',
            customer_notes: message.trim() || null,
            signed_by: signedBy.trim(),
            signature: dataUrl,
          })
        );
      } else if (job && booking && checkin) {
        dispatch(
          addFeedback({
            feedback: 'signature',
            customer_notes: message.trim() || null,
            signed_by: signedBy.trim(),
            signature: dataUrl,
            job_id: job.id,
            checkin_id: checkin.id,
            booking_id: booking.id,
            booking_date: booking.date,
            company_id: job.company_id,
          })
        );
      }

      toast.success("Signature saved successfully");
      navigate({ to: "/job/$jobId/$date/signoff/complete", params: { jobId, date } });
    } catch (error) {
      console.error("Error saving signature:", error);
      toast.error("Failed to save signature");
    }
  };

  if (!job || !booking) {
    return <div>Job not found</div>;
  }

  return (
    <>
      {/* Content */}
      <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
        <div className="p-8 flex items-center">
          <div className="max-w-4xl w-full space-y-8 mx-auto">
            <div className="flex-1">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Please sign-off the operatives work
                </h2>
              </div>

              {/* Tasks Section */}
              <div className="mb-6">
                <Label className="text-base font-medium mb-2 block">
                  Tasks
                </Label>
                <div className=" rounded-lg bg-gray-50 border border-gray-200 ">
                  <ScrollArea className="overflow-y-auto overflow-x-hidden h-60">
                    <div className="p-4">
                      <JobTaskListView
                        tasks={tasks}
                        ignoreTopPaddingAndBorder={true}
                        canUpdateTimesheet={false}
                        canAddPhotos={false}
                        updateJobStatus={false}
                        checkin={checkin}
                      />
                      {tasks.length === 0 && (
                        <p className="text-gray-500 text-center py-4">
                          No tasks found
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>

              {/* Signed By */}
              <div className="mb-6">
                <Label
                  htmlFor="signedBy"
                  className="text-base font-medium mb-2 block"
                >
                  Signed by...
                </Label>
                <Input
                  id="signedBy"
                  value={signedBy}
                  onChange={(e) => setSignedBy(e.target.value)}
                  placeholder="Type Name Here..."
                  className="max-w-md"
                />
              </div>

              {/* Signature Canvas */}
              <div className="mb-6 flex-1">
                <Label className="text-base font-medium mb-2 block">
                  Please sign here
                </Label>
                <div className="inline-flex border-2 rounded-lg bg-white border-neutral-200">
                <SignaturePad
                  ref={signaturePadRef}
                  width={600}
                  height={200}
                  placeholder="Sign here"
                  onSignatureChange={handleSignatureChange}
                  className="w-full"
                />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setIsMessageDialogOpen(true)}
                >
                  Add Message
                </Button>
                <Button
                  onClick={handleComplete}
                  disabled={!hasSignature || !signedBy.trim()}
                >
                  Complete
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Message Dialog */}
      <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Message</DialogTitle>
            <DialogDescription>
              Add an optional message or note.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your message here..."
            rows={4}
            className="mb-4"
          />

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsMessageDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={() => setIsMessageDialogOpen(false)}>
              Save Message
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
